import React from "react";
import { motion } from "framer-motion";
import { AiOutlineFileDone } from "react-icons/ai";
import { CiLock } from "react-icons/ci";
import { LuMessageSquareText } from "react-icons/lu";


export default function PrivacyPolicy() {
  const sections = [
    {
      title: "1. Introduction",
      content: `MapleCloud Technologies (“we,” “our,” “us”) respects your privacy and is committed to protecting it. This Privacy Policy explains how we collect, use, store, and disclose information, and the rights you have in relation to that information.

This policy applies to:
• Visitors to our website https://maplecloudtechnologies.com
• Customers, partners, and users of our services
• Individuals who interact with MapleCloud Technologies through communications, events, or support`
    },
    {
      title: "2. Categories of Data We Collect",
      content: `We distinguish between different types of data depending on how it is collected and used:

• Customer / Processor Data – Data we process on behalf of our customers (for example, information you store in our systems or applications).
• Business / Controller Data – Personal details: name, email, phone number, organization, job title; account information: login details, support tickets, subscription details; marketing information: preferences, event registrations, survey responses.
• Technical & Usage Data – IP address, browser type, operating system, device identifiers, access logs, cookies, session activity, referring URLs, and approximate geolocation (based on IP).
• Threat / Security Data – Logs, alerts, or indicators related to cybersecurity threats that help us improve protection and detection capabilities.`
    },
    {
      title: "3. How We Use Your Data",
      content: `• Providing Services – deliver, maintain, and support our products and website
• Account Management – billing, authentication, customer support
• Security – detect and prevent fraud, threats, abuse, or unauthorized access
• Analytics – understand usage patterns, improve our services and website performance
• Marketing (with consent) – send updates, offers, and newsletters you opt-in to receive
• Compliance – meet legal, contractual, and regulatory obligations`
    },
    {
      title: "4. How We Share Your Data",
      content: `We do not sell your personal information. We may share it with:
• Affiliates and subsidiaries within MapleCloud Technologies
• Service providers and partners (hosting, analytics, payment, marketing, security tools)
• Resellers or business partners involved in delivering our services to you
• Security researchers / CERTs for coordinated cybersecurity threat handling
• Authorities or regulators where required by law`
    },
    {
      title: "5. Cookies and Tracking Technologies",
      content: `We use cookies, web beacons, and similar technologies to:
• Recognize returning visitors
• Personalize your browsing experience
• Analyze traffic and site performance
• Deliver targeted content or ads (if applicable)

You can control cookies via your browser settings.`
    },
    {
      title: "6. Data Retention",
      content: `We retain personal data as long as needed for the purposes outlined in this policy:
• Account and business records: retained while the account is active plus a reasonable period afterward
• Security logs and threat data: may be retained longer to improve detection and compliance
• Backups: stored securely and deleted after defined retention cycles`
    },
    {
      title: "7. Data Security",
      content: `We apply administrative, technical, and physical safeguards, including:
• Encryption during transfer and at rest (where applicable)
• Secure access controls and authentication
• Firewalls, intrusion detection, and monitoring systems
• Regular security testing and audits

While we take strong precautions, no system can be guaranteed 100% secure.`
    },
    {
      title: "8. Your Rights",
      content: `Depending on your location, you may have rights to:
• Access, update, or delete your personal data
• Restrict or object to processing
• Withdraw consent for marketing communications
• Request data portability

To exercise these rights, contact us at compliance@maplecloudtechnologies.com.`
    },
    {
      title: "9. Children’s Privacy",
      content: `Our website and services are not directed to individuals under 18. We do not knowingly collect information from children.`
    },
    {
      title: "10. Aggregate and De-Identified Data",
      content: `We may use or share aggregated or anonymized data (which cannot identify you) for analytics, research, or improving our offerings.`
    },
    {
      title: "11. Changes to This Policy",
      content: `We may update this Privacy Policy occasionally. Updates will be posted on this page with an updated “Effective Date.” Significant changes may also be communicated via email or notices on our website.`
    },
    {
      title: "12. Contact Us",
      content: `MapleCloud Technologies
Email: Compliance@maplecloudtechnologies.com
Phone: +91-8178803636
Address: 203, D-219, Sector -63, Noida-201301, India`
    }
  ];

  return (
  <div className="pt-10 pb-5 bg-gray-900 w-full text-gray-100">
{/* Hero Section with floating icons */}
<div className="max-w-7xl mx-auto px-6 pt-12 pb-8">
<div className="bg-gray-800 rounded-2xl shadow-xl relative overflow-hidden">
<div className="absolute inset-y-0 left-0 w-3 bg-cyan-500" />
<div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 p-10 items-center">
<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
<h1 className="text-4xl md:text-5xl font-extrabold text-white">Privacy Policy</h1>
<p className="mt-3 text-gray-300">Effective Date: <strong>13th September 2025</strong> | Last Updated: <strong>13th September 2025</strong></p>
<p className="mt-5 text-gray-400 max-w-lg">We respect your privacy. This page explains what we collect, why we collect it, and how we protect it.</p>
<div className="mt-6 flex md:flex-row text-center flex-col gap-3 ">
<a href="mailto:Compliance@maplecloudtechnologies.com" className="px-5 py-2 text-sm bg-gray-700 border border-gray-600 rounded-full shadow hover:bg-gray-600">Email Us</a>
<a  href="tel:+918178803636" className="px-5 py-2 text-sm bg-cyan-500 text-black rounded-full shadow hover:bg-cyan-400">Contact Compliance</a>
</div>
</motion.div>


<motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="hidden md:flex justify-center">
<div className="relative w-80 h-56">
<div className="absolute text-3xl -left-8 text-pink-300 -top-6 w-20 h-20 rounded-lg bg-cyan-900 border border-cyan-700 flex items-center justify-center"><AiOutlineFileDone/></div>
<div className="absolute text-4xl text-yellow-300 right-0 top-0 w-24 h-24 rounded-lg bg-cyan-800 border border-cyan-600 flex items-center justify-center"><CiLock/></div>
<div className="absolute left-12 text-white-400 text-2xl bottom-6 w-16 h-16 rounded-lg bg-cyan-700 border border-cyan-500 flex items-center justify-center"><LuMessageSquareText/></div>
</div>
</motion.div>
</div>
</div>
</div>


{/* Main layout */}
<div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
{/* Left TOC */}
<nav className="lg:col-span-3">
<div className="sticky top-28 bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-700">
<h3 className="text-lg font-semibold text-cyan-400">Contents</h3>
<ul className="mt-4 space-y-2 text-sm text-gray-300">
{sections.map((s) => (
<li key={s.title}><a href={`#${s.title}`} className="hover:text-cyan-400">{s.title}</a></li>
))}
</ul>
{/* <div className="mt-6 border-t border-gray-700 pt-4 text-sm text-gray-400">
<div className="mb-2">Download</div>
<div className="flex gap-2">
<a href="/Privacy_Policy.docx" download className="flex-1 text-center px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600">DOCX</a>
<a href="/Privacy_Policy.pdf" download className="flex-1 text-center px-3 py-2 bg-cyan-500 text-black rounded-md hover:bg-cyan-400">PDF</a>
</div>
</div> */}
</div>
</nav>


{/* Main content */}
<main className="lg:col-span-6 bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-700">
{sections.map((sec) => (
<section key={sec.title} id={sec.title} className="mb-8">
<h2 className="text-xl font-semibold text-cyan-400">{sec.title}</h2>
<p className="mt-3 whitespace-pre-line text-sm text-gray-300">{sec.content}</p>
</section>
))}
</main>


{/* Right column quick contact */}
<aside className="lg:col-span-3">
<div className="sticky top-28 bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-700">
<h4 className="font-semibold text-cyan-400">Need Help?</h4>
<p className="mt-3 text-sm text-gray-300">Use the contents to navigate or reach out and we'll respond within 2-3 business days.</p>
<div className="mt-5">
<a href="mailto:Compliance@maplecloudtechnologies.com" className="block w-full text-center px-4 py-2 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600">Email Compliance</a>
<a href="tel:+918178803636" className="mt-3 block w-full text-center px-4 py-2 bg-cyan-500 text-black rounded-md hover:bg-cyan-400">Call Us</a>
</div>
<div className="mt-6 text-xs text-gray-400">203, D-219, Sector -63, Noida-201301, India</div>
</div>
</aside>
</div>
</div>
  );
}